import React, { useEffect } from "react";
import { Mail, Phone, MessageCircle, Instagram, Clock } from "lucide-react";
import { LegalLayout, LegalH2, LegalP } from "../components/LegalLayout";

const Support = () => {
    useEffect(() => {
        document.title = "Support — Voro";
        window.scrollTo(0, 0);
    }, []);

    return (
        <LegalLayout testId="page-support" title="Support" updated="16 June 2026">
            <LegalP>
                Need help with Voro? We're a small team based in Perth, Western Australia, and
                we read every message ourselves. Reach out using any of the channels below and
                we'll get back to you as quickly as we can.
            </LegalP>

            <div className="grid gap-4 sm:grid-cols-2 not-prose mt-6">
                <a
                    href="mailto:hello@voro.au"
                    data-testid="support-email-card"
                    className="group block rounded-2xl border border-white/10 bg-white/[0.03] p-5 hover:border-violet-400/40 hover:bg-violet-500/[0.06] transition-colors"
                >
                    <div className="flex items-center gap-2 text-violet-300">
                        <Mail size={16} />
                        <span className="text-xs uppercase tracking-[0.18em] font-semibold">
                            Email
                        </span>
                    </div>
                    <div className="mt-2 font-display text-lg font-bold text-white group-hover:text-violet-200">
                        hello@voro.au
                    </div>
                    <div className="mt-1 text-sm text-white/55">
                        Best for product questions, bug reports, account issues, and partnerships.
                    </div>
                </a>

                <a
                    href="tel:+61466960644"
                    data-testid="support-phone-card"
                    className="group block rounded-2xl border border-white/10 bg-white/[0.03] p-5 hover:border-violet-400/40 hover:bg-violet-500/[0.06] transition-colors"
                >
                    <div className="flex items-center gap-2 text-violet-300">
                        <Phone size={16} />
                        <span className="text-xs uppercase tracking-[0.18em] font-semibold">
                            Phone
                        </span>
                    </div>
                    <div className="mt-2 font-display text-lg font-bold text-white group-hover:text-violet-200">
                        +61 466 960 644
                    </div>
                    <div className="mt-1 text-sm text-white/55">
                        Mon–Fri, 9am–5pm AWST (Perth time).
                    </div>
                </a>

                <a
                    href="https://www.instagram.com/vorofitness/"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="support-instagram-card"
                    className="group block rounded-2xl border border-white/10 bg-white/[0.03] p-5 hover:border-violet-400/40 hover:bg-violet-500/[0.06] transition-colors"
                >
                    <div className="flex items-center gap-2 text-violet-300">
                        <Instagram size={16} />
                        <span className="text-xs uppercase tracking-[0.18em] font-semibold">
                            Instagram
                        </span>
                    </div>
                    <div className="mt-2 font-display text-lg font-bold text-white group-hover:text-violet-200">
                        @vorofitness
                    </div>
                    <div className="mt-1 text-sm text-white/55">
                        DM us — we respond to most messages within a day.
                    </div>
                </a>

                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                    <div className="flex items-center gap-2 text-violet-300">
                        <Clock size={16} />
                        <span className="text-xs uppercase tracking-[0.18em] font-semibold">
                            Response time
                        </span>
                    </div>
                    <div className="mt-2 font-display text-lg font-bold text-white">
                        Within 1–2 business days
                    </div>
                    <div className="mt-1 text-sm text-white/55">
                        We aim to reply to every support request within 48 hours, often much
                        sooner.
                    </div>
                </div>
            </div>

            <LegalH2>Common questions</LegalH2>

            <div className="space-y-5">
                <div>
                    <h3 className="font-display text-base font-bold text-white">
                        How do I join the Voro waitlist?
                    </h3>
                    <p className="mt-1.5 text-white/70 text-[15px]">
                        Head to{" "}
                        <a href="https://voro.au" className="text-violet-300 hover:underline">
                            voro.au
                        </a>{" "}
                        and enter your name and email in the waitlist form. You'll be notified
                        when early access opens.
                    </p>
                </div>

                <div>
                    <h3 className="font-display text-base font-bold text-white">
                        When is Voro launching?
                    </h3>
                    <p className="mt-1.5 text-white/70 text-[15px]">
                        We're rolling out to Perth first, with early access for waitlist members
                        in the coming months. Follow{" "}
                        <a
                            href="https://www.instagram.com/vorofitness/"
                            target="_blank"
                            rel="noreferrer"
                            className="text-violet-300 hover:underline"
                        >
                            @vorofitness
                        </a>{" "}
                        for launch updates.
                    </p>
                </div>

                <div>
                    <h3 className="font-display text-base font-bold text-white">
                        I'm a gym, club, or studio — how do I partner with Voro?
                    </h3>
                    <p className="mt-1.5 text-white/70 text-[15px]">
                        Switch the waitlist toggle to <strong>Community</strong> on{" "}
                        <a href="https://voro.au" className="text-violet-300 hover:underline">
                            voro.au
                        </a>{" "}
                        and add your community name, or email us directly at{" "}
                        <a
                            href="mailto:hello@voro.au"
                            className="text-violet-300 hover:underline"
                        >
                            hello@voro.au
                        </a>
                        .
                    </p>
                </div>

                <div>
                    <h3 className="font-display text-base font-bold text-white">
                        I want to delete my data — how?
                    </h3>
                    <p className="mt-1.5 text-white/70 text-[15px]">
                        Email{" "}
                        <a
                            href="mailto:hello@voro.au"
                            className="text-violet-300 hover:underline"
                        >
                            hello@voro.au
                        </a>{" "}
                        from the address you signed up with and we'll remove your information,
                        as outlined in our{" "}
                        <a href="/privacy" className="text-violet-300 hover:underline">
                            Privacy Policy
                        </a>
                        .
                    </p>
                </div>

                <div>
                    <h3 className="font-display text-base font-bold text-white">
                        I found a bug or have feedback.
                    </h3>
                    <p className="mt-1.5 text-white/70 text-[15px]">
                        Amazing — that's exactly the kind of message we love. Email{" "}
                        <a
                            href="mailto:hello@voro.au"
                            className="text-violet-300 hover:underline"
                        >
                            hello@voro.au
                        </a>{" "}
                        with as much detail as you can (screenshots are great) and we'll dig in.
                    </p>
                </div>
            </div>

            <LegalH2>Still stuck?</LegalH2>
            <LegalP>
                If none of the above answered your question, send us a note at{" "}
                <a href="mailto:hello@voro.au" className="text-violet-300 hover:underline">
                    hello@voro.au
                </a>{" "}
                — a real human will reply.
            </LegalP>

            <div className="mt-10 rounded-2xl border border-white/10 bg-white/[0.03] p-5 not-prose">
                <div className="flex items-center gap-2 text-violet-300">
                    <MessageCircle size={16} />
                    <span className="text-xs uppercase tracking-[0.18em] font-semibold">
                        Voro Pty Ltd
                    </span>
                </div>
                <div className="mt-2 text-sm text-white/65 leading-relaxed">
                    Perth, Western Australia<br />
                    Email:{" "}
                    <a href="mailto:hello@voro.au" className="text-violet-300 hover:underline">
                        hello@voro.au
                    </a>
                    <br />
                    Phone:{" "}
                    <a href="tel:+61466960644" className="text-violet-300 hover:underline">
                        +61 466 960 644
                    </a>
                </div>
            </div>
        </LegalLayout>
    );
};

export default Support;
